import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, Users, Calendar, CheckCircle, XCircle, Clock, Settings, KeyRound, Copy, Download, QrCode, UserX, UserCheck, Trash2, Edit } from 'lucide-react';
import { saveAs } from 'file-saver';
import AdminProfileEditor from '@/components/AdminProfileEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrokerDetail = () => {
  const navigate = useNavigate();
  const { brokerId } = useParams();
  const [broker, setBroker] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchBrokerDetails();
  }, [brokerId]);

  const fetchBrokerDetails = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/brokers/${brokerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroker(response.data);
      
      // Fetch agents for this broker
      const agentsResponse = await axios.get(`${API}/admin/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const brokerAgents = agentsResponse.data.agents.filter(agent => agent.broker_id === brokerId);
      setAgents(brokerAgents);
      
      // Fetch QR code for this broker
      fetchQRCode(response.data.id);
    } catch (error) {
      toast.error('Failed to fetch broker details');
      if (error.response?.status === 404) {
        navigate('/admin/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchQRCode = async (brokerId) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/brokers/${brokerId}/qr`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCode(response.data.qr_code);
      setQrUrl(response.data.url);
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
    }
  };

  const downloadQRCode = () => {
    if (qrCode) {
      const byteCharacters = atob(qrCode);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      saveAs(blob, `broker-${broker?.name?.replace(/\s+/g, '-')}-qr-code.png`);
      toast.success('QR code downloaded!');
    }
  };

  const handleSettingToggle = async (settingName, value) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.patch(
        `${API}/admin/brokers/${brokerId}/settings`,
        { [settingName]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBroker(response.data);
      toast.success('Broker settings updated successfully');
    } catch (error) {
      toast.error('Failed to update broker settings');
      console.error(error);
    }
  };

  const handleResetPassword = async (userId, userName, userType) => {
    if (!window.confirm(`Reset password for ${userName} (${userType})? A new random password will be generated.`)) {
      return;
    }

    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResetPasswordData({
        userName,
        userType,
        newPassword: response.data.new_password
      });
      setShowPasswordDialog(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const copyPasswordToClipboard = () => {
    if (resetPasswordData?.newPassword) {
      navigator.clipboard.writeText(resetPasswordData.newPassword);
      toast.success('Password copied to clipboard!');
    }
  };

  const handleApprove = async () => {
    if (!window.confirm(`Approve broker ${broker.name}?`)) return;
    setActionLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API}/admin/brokers/${brokerId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Broker approved');
      fetchBrokerDetails();
    } catch (error) {
      toast.error('Failed to approve broker');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    const action = broker.status === 'active' ? 'deactivate' : 'activate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} broker ${broker.name}?`)) return;
    setActionLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(`${API}/admin/brokers/${brokerId}/status`, {
        status: broker.status === 'active' ? 'inactive' : 'active'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Broker ${action}d`);
      fetchBrokerDetails();
    } catch (error) {
      toast.error(`Failed to ${action} broker`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleTogglePremium = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(`${API}/admin/brokers/${brokerId}/premium`, {
        premium: !broker.premium
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Premium ${!broker.premium ? 'enabled' : 'disabled'}`);
      fetchBrokerDetails();
    } catch (error) {
      toast.error('Failed to update premium status');
    }
  };

  const handleDeleteBroker = async () => {
    setDeleteLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/brokers/${brokerId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { passcode: deletePasscode }
      });
      toast.success('Broker deleted');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      active: 'bg-green-100 text-green-800 border-green-300',
      inactive: 'bg-red-100 text-red-800 border-red-300'
    };
    const icons = {
      pending: <Clock size={14} />,
      active: <CheckCircle size={14} />,
      inactive: <XCircle size={14} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status]}`}>
        {icons[status]}
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <p className="text-stone-500">Loading...</p>
      </div>
    );
  }

  if (!broker) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <p className="text-stone-500">Broker not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="ghost"
            className="mb-4 text-stone-600 hover:text-[#1A4D2E]"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
            Broker Details
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Broker Info Card */}
        <Card className="bg-white p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-stone-800 mb-2">{broker.name}</h2>
              <p className="text-sm text-stone-500 mb-4">ID: {broker.id}</p>
            </div>
            {getStatusBadge(broker.status)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-stone-500">Email</p>
                <p className="font-medium text-stone-800">{broker.email}</p>
              </div>
            </div>

            {broker.phone && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Phone className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-xs text-stone-500">Phone</p>
                  <p className="font-medium text-stone-800">{broker.phone}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-stone-500">Total Agents</p>
                <p className="font-medium text-stone-800">{agents.length}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Calendar className="text-orange-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-stone-500">Joined</p>
                <p className="font-medium text-stone-800">
                  {new Date(broker.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-stone-200 space-y-4">
            {/* Premium Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-stone-700">Premium Status</Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={broker.premium || false}
                  onCheckedChange={handleTogglePremium}
                  className="data-[state=checked]:bg-[#1A4D2E]"
                  data-testid="broker-premium-toggle"
                />
                <span className="text-sm text-stone-600">{broker.premium ? 'On' : 'Off'}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {broker.status === 'pending' && (
                <Button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="approve-broker-btn"
                >
                  <CheckCircle size={18} className="mr-2" />
                  Approve Broker
                </Button>
              )}

              {broker.status !== 'pending' && (
                <Button
                  onClick={handleToggleStatus}
                  disabled={actionLoading}
                  variant={broker.status === 'active' ? 'destructive' : 'default'}
                  data-testid="toggle-status-btn"
                >
                  {broker.status === 'active' ? (
                    <><UserX size={18} className="mr-2" />Deactivate</>
                  ) : (
                    <><UserCheck size={18} className="mr-2" />Activate</>
                  )}
                </Button>
              )}

              <Button
                onClick={() => handleResetPassword(broker.id, broker.name, 'Broker')}
                variant="outline"
                className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                data-testid="reset-broker-pwd-btn"
              >
                <KeyRound size={18} className="mr-2" />
                Reset Password
              </Button>

              <Button
                onClick={() => setShowProfileEditor(true)}
                variant="outline"
                className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E]/10"
                data-testid="edit-public-profile-btn"
              >
                <Edit size={18} className="mr-2" />
                Edit Public Profile
              </Button>

              <Button
                onClick={() => setShowDeleteDialog(true)}
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="delete-broker-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Delete Broker
              </Button>
            </div>
          </div>
        </Card>

        {/* Agent Invitation QR Code Card */}
        <Card className="bg-white p-6">
          <div className="flex items-center gap-3 mb-6">
            <QrCode className="text-[#1A4D2E]" size={24} />
            <h2 className="text-xl font-bold text-stone-800">Agent Invitation QR Code</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* QR Code Display */}
            <div className="flex flex-col items-center justify-center bg-stone-50 p-6 rounded-lg border-2 border-stone-200">
              {qrCode ? (
                <>
                  <img 
                    src={`data:image/png;base64,${qrCode}`} 
                    alt="Agent Invitation QR Code"
                    className="w-64 h-64 mb-4"
                  />
                  <p className="text-xs text-stone-500 text-center mb-4">
                    Agents can scan this QR code to join {broker.name}
                  </p>
                  <Button
                    onClick={downloadQRCode}
                    className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                  >
                    <Download size={18} className="mr-2" />
                    Download QR Code
                  </Button>
                </>
              ) : (
                <div className="text-center text-stone-500">
                  <QrCode size={48} className="mx-auto mb-2 opacity-30" />
                  <p>Loading QR code...</p>
                </div>
              )}
            </div>

            {/* Invitation URL */}
            <div className="flex flex-col justify-center">
              <Label className="text-sm font-semibold text-stone-800 mb-3">
                Agent Invitation URL
              </Label>
              <div className="bg-stone-50 p-4 rounded-lg border-2 border-stone-200 mb-4">
                <p className="text-sm text-stone-600 break-all font-mono">
                  {qrUrl || 'Loading...'}
                </p>
              </div>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(qrUrl);
                  toast.success('URL copied to clipboard!');
                }}
                variant="outline"
                className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                disabled={!qrUrl}
              >
                <Copy size={18} className="mr-2" />
                Copy URL
              </Button>
              
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Note:</strong> Share this QR code or URL with agents who want to join this broker. They will be able to register and request to join.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Broker Settings Card */}
        <Card className="bg-white p-6">
          <div className="flex items-center gap-3 mb-6">
            <Settings className="text-[#1A4D2E]" size={24} />
            <h2 className="text-xl font-bold text-stone-800">Broker Settings</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
              <div className="flex-1">
                <Label className="text-sm font-semibold text-stone-800 mb-1 block">
                  Show Open House Requests
                </Label>
                <p className="text-xs text-stone-500">
                  When enabled, the broker can see and manage open house requests on their dashboard
                </p>
              </div>
              <Switch
                checked={broker?.show_open_house_requests ?? true}
                onCheckedChange={(checked) => handleSettingToggle('show_open_house_requests', checked)}
                className="ml-4"
              />
            </div>
          </div>
        </Card>

        {/* Agents List */}
        <Card className="bg-white overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200">
            <h2 className="text-xl font-bold text-stone-800">Agents ({agents.length})</h2>
          </div>
          
          {agents.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              No agents linked to this broker yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="w-full">
                <div className="grid grid-cols-[1.5fr_2fr_1.2fr_1fr_1fr_1.5fr] bg-stone-50 border-b border-stone-200">
                  <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Name</div>
                  <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Email</div>
                  <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Phone</div>
                  <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Status</div>
                  <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Joined</div>
                  <div className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase">Actions</div>
                </div>
                <div className="divide-y divide-stone-200">
                  {agents.map((agent) => (
                    <div key={agent.id} className="grid grid-cols-[1.5fr_2fr_1.2fr_1fr_1fr_1.5fr] items-center hover:bg-stone-50">
                      <div className="px-6 py-4 font-medium text-stone-800">{agent.name}</div>
                      <div className="px-6 py-4 text-stone-600">{agent.email}</div>
                      <div className="px-6 py-4 text-stone-600">{agent.phone || 'N/A'}</div>
                      <div className="px-6 py-4">{getStatusBadge(agent.status)}</div>
                      <div className="px-6 py-4 text-stone-600">
                        {new Date(agent.created_at).toLocaleDateString()}
                      </div>
                      <div className="px-6 py-4 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResetPassword(agent.id, agent.name, 'Agent')}
                          className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                        >
                          <KeyRound size={16} className="mr-1" />
                          Reset
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">Password Reset Successful</DialogTitle>
            <DialogDescription>
              New password generated for <span className="font-semibold">{resetPasswordData?.userName}</span> ({resetPasswordData?.userType})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-stone-50 p-4 rounded-lg border-2 border-[#1A4D2E]">
              <p className="text-xs text-stone-500 mb-2">New Password:</p>
              <div className="flex items-center justify-between gap-2">
                <code className="text-lg font-mono font-bold text-[#1A4D2E] break-all">
                  {resetPasswordData?.newPassword}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyPasswordToClipboard}
                  className="shrink-0"
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Important:</strong> Save this password securely. Share it with the user through a secure channel. This password will not be shown again.
              </p>
            </div>
            
            <Button
              onClick={() => setShowPasswordDialog(false)}
              className="w-full bg-[#1A4D2E] text-white hover:bg-[#143D24]"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600">Delete Broker</DialogTitle>
            <DialogDescription>
              This will permanently delete {broker?.name}. Enter admin passcode to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input
              type="password"
              placeholder="Enter admin passcode"
              value={deletePasscode}
              onChange={(e) => setDeletePasscode(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg bg-white text-sm"
              data-testid="delete-passcode-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeletePasscode(''); }} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleDeleteBroker}
                disabled={deleteLoading || !deletePasscode}
                variant="destructive"
                className="flex-1"
                data-testid="confirm-delete-btn"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AdminProfileEditor
        open={showProfileEditor}
        onClose={(saved) => { setShowProfileEditor(false); if (saved) fetchBrokerData(); }}
        userId={brokerId}
        userType="agent"
      />
    </div>
  );
};

export default BrokerDetail;
